// ═══════════════════════════════════════════════════════════════
// SHOWS VIEW — Shows & Sponsorships (4-tab view)
// Path: src/views/ShowsView.jsx
//
// Zone Framework compliant. All roles.
//   Tab 1: Shows         (Phase 4 — active)
//   Tab 2: Packages      (Phase 4 — active)
//   Tab 3: Sponsorships  (Phase 5 — active)
//   Tab 4: Benefit Templates (Phase 3 — active)
//
// Blueprint: CMM_Show_Sponsorship_Blueprint_v1_1.md
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Film,
  Theater,
  Package,
  Users,
  BookOpen,
  Plus,
  Search,
  Filter,
  Calendar,
  MapPin,
  ChevronDown,
  ChevronUp,
  Archive,
  Edit3,
  DollarSign,
  Eye,
  EyeOff,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, CHAPTER_ID, ICON_SIZES } from "../constants/appConfig";
import { HELP } from "../constants/helpContent";
import { ViewLayout } from "../components/ui/ViewLayout";
import {
  ZoneOneHeader,
  ZoneThreeFooter,
} from "../components/zones/ZoneHeaderFooter";
import { ZoneTwoToolbar } from "../components/zones/ZoneTwoToolbar";
import { TabBar } from "../components/ui/TabBar";
import { Card } from "../components/ui/Card";
import { ActionButton } from "../components/ui/ActionButton";
import { ConfirmDialog } from "../components/ui/Modal";
import { BenefitTemplateFormModal } from "../components/shows/BenefitTemplateFormModal";
import { BenefitTemplateRow } from "../components/shows/BenefitTemplateRow";
import { ShowFormModal } from "../components/shows/ShowFormModal";
import { PackageFormModal } from "../components/shows/PackageFormModal";
import { SponsorshipCard } from "../components/shows/SponsorshipCard";
import {
  benefitTemplatesService,
  showsService,
  packagesService,
  sponsorshipsService,
} from "../services/showSponsorshipsService";

// ── Tab definitions ─────────────────────────────────────────────

const TABS = [
  { id: "shows", label: "Shows", icon: Theater },
  { id: "packages", label: "Packages", icon: Package },
  { id: "sponsorships", label: "Sponsorships", icon: Users },
  { id: "templates", label: "Benefit Templates", icon: BookOpen },
];

// ── Shared helpers ──────────────────────────────────────────────

const STATUS_COLORS = {
  upcoming: { bg: "#dbeafe", color: "#1d4ed8", label: "Upcoming" },
  active: { bg: "#dcfce7", color: "#16a34a", label: "Active" },
  closed: { bg: "#f3f4f6", color: "#6b7280", label: "Closed" },
  archived: { bg: "#fef3c7", color: "#92400e", label: "Archived" },
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatCurrency = (amount) => {
  if (amount == null) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// ═══════════════════════════════════════════════════════════════
// SHOWS VIEW — Main Component
// ═══════════════════════════════════════════════════════════════

export const ShowsView = () => {
  const { palette, navigate, isAdmin, isDirector, userProfile, showMessage, showLoading, hideLoading, canViewGifts } =
    useApp();

  const [activeTab, setActiveTab] = useState("shows");
  const canEdit = isAdmin || isDirector;

  return (
    <ViewLayout>
      {/* Zone 1 — Navigation */}
      <ZoneOneHeader
        title="Shows & Sponsorships"
        subtitle="Manage productions, packages, and sponsor benefits"
        icon={Film}
        onBack={() => navigate("main")}
        onHome={() => navigate("main")}
        helpContent={HELP.shows}
      />
      <ZoneTwoToolbar />

      {/* Zone 2 — Tabs */}
      <TabBar tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Content area */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
        {activeTab === "shows" && (
          <ShowsTab
            canEdit={canEdit}
            isAdmin={isAdmin}
            userProfile={userProfile}
            showMessage={showMessage}
            showLoading={showLoading}
            hideLoading={hideLoading}
            palette={palette}
          />
        )}
        {activeTab === "packages" && (
          <PackagesTab
            canEdit={canEdit}
            isAdmin={isAdmin}
            userProfile={userProfile}
            showMessage={showMessage}
            showLoading={showLoading}
            hideLoading={hideLoading}
            palette={palette}
          />
        )}
        {activeTab === "sponsorships" && (
          <SponsorshipsTab
            canEdit={canEdit}
            isAdmin={isAdmin}
            canViewGifts={canViewGifts}
            userProfile={userProfile}
            showMessage={showMessage}
            showLoading={showLoading}
            hideLoading={hideLoading}
            palette={palette}
          />
        )}
        {activeTab === "templates" && (
          <BenefitTemplatesTab
            canEdit={canEdit}
            canDelete={isAdmin}
            userProfile={userProfile}
            showMessage={showMessage}
            showLoading={showLoading}
            hideLoading={hideLoading}
            palette={palette}
          />
        )}
      </div>

      {/* Zone 3 — Footer */}
      <ZoneThreeFooter />
    </ViewLayout>
  );
};


// ═══════════════════════════════════════════════════════════════
// TAB 1: SHOWS
// ═══════════════════════════════════════════════════════════════

const ShowsTab = ({ canEdit, isAdmin, userProfile, showMessage, showLoading, hideLoading, palette }) => {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingShow, setEditingShow] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const loadShows = useCallback(async () => {
    try {
      setLoading(true);
      showLoading("Loading shows...");
      const data = showArchived
        ? await showsService.getAllShows()
        : await showsService.getShows();
      setShows(data);
    } catch (error) {
      showMessage("Failed to load shows", "error");
    } finally {
      setLoading(false);
      hideLoading();
    }
  }, [showMessage, showArchived, showLoading, hideLoading]);

  useEffect(() => {
    loadShows();
  }, [loadShows]);

  const handleSave = async (showData) => {
    if (editingShow) {
      await showsService.updateShow(editingShow.id, showData);
      showMessage("Show updated");
    } else {
      await showsService.createShow(
        showData,
        CHAPTER_ID,
        userProfile?.id
      );
      showMessage("Show created");
    }
    await loadShows();
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      await showsService.archiveShow(archiveTarget.id);
      showMessage("Show archived");
      setArchiveTarget(null);
      await loadShows();
    } catch (error) {
      showMessage("Failed to archive show", "error");
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: palette.text3, fontFamily: FONT_FAMILY }}>
        Loading shows...
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <label style={{
          display: "flex", alignItems: "center", gap: 6,
          fontSize: FONT_SIZES.xs, color: palette.text2, fontFamily: FONT_FAMILY, cursor: "pointer",
        }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} style={{ cursor: "pointer" }} />
          Show archived
        </label>
        <div style={{ flex: 1 }} />
        {canEdit && (
          <ActionButton label="Add Show" icon={Plus} small onClick={() => { setEditingShow(null); setShowFormModal(true); }} />
        )}
      </div>

      {/* Show cards */}
      {shows.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 40 }}>
          <Theater size={ICON_SIZES.xxl} style={{ color: palette.text3, marginBottom: 8 }} />
          <p style={{ color: palette.text2, fontFamily: FONT_FAMILY, fontSize: FONT_SIZES.sm, margin: "8px 0 0" }}>
            No shows yet. Add your first production to get started.
          </p>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {shows.map((show) => {
            const statusInfo = STATUS_COLORS[show.status] || STATUS_COLORS.upcoming;
            return (
              <Card key={show.id} style={{ padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                      <h3 style={{ margin: 0, fontSize: FONT_SIZES.md, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                        {show.title}
                      </h3>
                      <span style={{
                        fontSize: "0.65rem", fontWeight: 600, padding: "2px 8px", borderRadius: 10,
                        background: statusInfo.bg, color: statusInfo.color, fontFamily: FONT_FAMILY,
                      }}>
                        {statusInfo.label}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: FONT_SIZES.xs, color: palette.text2, fontFamily: FONT_FAMILY }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <Calendar size={ICON_SIZES.xs} /> Season: {show.season}
                      </span>
                      {show.venue && (
                        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <MapPin size={ICON_SIZES.xs} /> {show.venue}
                        </span>
                      )}
                      {(show.open_date || show.close_date) && (
                        <span>
                          {formatDate(show.open_date)}{show.open_date && show.close_date && " – "}{formatDate(show.close_date)}
                        </span>
                      )}
                    </div>
                    {show.description && (
                      <p style={{ margin: "6px 0 0", fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY }}>
                        {show.description}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  {canEdit && show.status !== "archived" && (
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <ActionButton label="Edit" icon={Edit3} small variant="secondary" onClick={() => { setEditingShow(show); setShowFormModal(true); }} />
                      <ActionButton label="Archive" icon={Archive} small variant="secondary" onClick={() => setArchiveTarget(show)} />
                    </div>
                  )}
                  {canEdit && show.status === "archived" && (
                    <ActionButton label="Edit" icon={Edit3} small variant="secondary" onClick={() => { setEditingShow(show); setShowFormModal(true); }} />
                  )}
                </div>
              </Card>
            );
          })}
          <p style={{ textAlign: "center", fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY, margin: "8px 0 0" }}>
            {shows.length} show{shows.length !== 1 ? "s" : ""}
          </p>
        </div>
      )}

      {/* Modals */}
      {showFormModal && (
        <ShowFormModal
          show={editingShow}
          onSave={handleSave}
          onClose={() => { setShowFormModal(false); setEditingShow(null); }}
        />
      )}
      {archiveTarget && (
        <ConfirmDialog
          title={`Archive "${archiveTarget.title}"?`}
          message="Archived shows are hidden by default. Existing sponsorships are not affected."
          confirmLabel="Archive"
          variant="secondary"
          onConfirm={handleArchive}
          onCancel={() => setArchiveTarget(null)}
        />
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// TAB 2: PACKAGES
// ═══════════════════════════════════════════════════════════════

const PackagesTab = ({ canEdit, isAdmin, userProfile, showMessage, showLoading, hideLoading, palette }) => {
  const [packages, setPackages] = useState([]);
  const [shows, setShows] = useState([]);
  const [catalogTemplates, setCatalogTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [expandedPkg, setExpandedPkg] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      showLoading("Loading packages...");
      const [pkgData, showData, templateData] = await Promise.all([
        packagesService.getPackages(),
        showsService.getShows(),
        benefitTemplatesService.getBenefitTemplates(false),
      ]);
      setPackages(pkgData);
      setShows(showData);
      setCatalogTemplates(templateData);
    } catch (error) {
      showMessage("Failed to load packages", "error");
    } finally {
      setLoading(false);
      hideLoading();
    }
  }, [showMessage, showLoading, hideLoading]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async (packageData) => {
    if (editingPackage) {
      await packagesService.updatePackage(editingPackage.id, packageData);
      showMessage("Package updated");
    } else {
      await packagesService.createPackage(
        packageData,
        CHAPTER_ID,
        userProfile?.id
      );
      showMessage("Package created");
    }
    await loadData();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await packagesService.deletePackage(deleteTarget.id);
      showMessage("Package deleted", "warning");
      setDeleteTarget(null);
      await loadData();
    } catch (error) {
      showMessage(error.message || "Failed to delete package", "error");
    }
  };

  // Group packages: Global first, then by show
  const groupedPackages = useMemo(() => {
    const groups = [];

    const globalPkgs = packages.filter((p) => !p.show_id);
    if (globalPkgs.length > 0) {
      groups.push({ label: "Global Packages (All Shows)", showId: null, packages: globalPkgs });
    }

    const showMap = new Map();
    shows.forEach((s) => showMap.set(s.id, s));

    const byShow = {};
    packages
      .filter((p) => p.show_id)
      .forEach((p) => {
        if (!byShow[p.show_id]) byShow[p.show_id] = [];
        byShow[p.show_id].push(p);
      });

    Object.entries(byShow).forEach(([showId, pkgs]) => {
      const show = showMap.get(showId);
      groups.push({
        label: show ? `${show.title} (${show.season})` : "Unknown Show",
        showId,
        packages: pkgs,
      });
    });

    return groups;
  }, [packages, shows]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: palette.text3, fontFamily: FONT_FAMILY }}>
        Loading packages...
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
        <div style={{ flex: 1 }} />
        {canEdit && (
          <ActionButton label="Add Package" icon={Plus} small onClick={() => { setEditingPackage(null); setShowFormModal(true); }} />
        )}
      </div>

      {/* Package groups */}
      {packages.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 40 }}>
          <Package size={ICON_SIZES.xxl} style={{ color: palette.text3, marginBottom: 8 }} />
          <p style={{ color: palette.text2, fontFamily: FONT_FAMILY, fontSize: FONT_SIZES.sm, margin: "8px 0 0" }}>
            No packages yet. Add your first sponsorship package to get started.
          </p>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {groupedPackages.map((group) => (
            <div key={group.showId || "global"}>
              <h4 style={{
                margin: "0 0 8px", fontSize: FONT_SIZES.sm, fontWeight: 700,
                color: palette.text2, fontFamily: FONT_FAMILY,
                borderBottom: `1px solid ${palette.cardBorder}`, paddingBottom: 6,
              }}>
                {group.label}
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {group.packages.map((pkg) => {
                  const isExpanded = expandedPkg === pkg.id;
                  const benefitList = pkg.benefits || [];
                  return (
                    <Card key={pkg.id} style={{ padding: 14, opacity: pkg.is_active ? 1 : 0.65 }}>
                      <div
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, cursor: "pointer" }}
                        onClick={() => setExpandedPkg(isExpanded ? null : pkg.id)}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                              {pkg.name}
                            </span>
                            <span style={{
                              display: "flex", alignItems: "center", gap: 3,
                              fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.accent1, fontFamily: FONT_FAMILY,
                            }}>
                              <DollarSign size={ICON_SIZES.xs} />
                              {formatCurrency(pkg.min_amount)}+
                            </span>
                            <span style={{
                              fontSize: "0.65rem", fontWeight: 600, color: palette.text3,
                              background: `${palette.text3}15`, padding: "2px 8px", borderRadius: 10, fontFamily: FONT_FAMILY,
                            }}>
                              {benefitList.length} benefit{benefitList.length !== 1 ? "s" : ""}
                            </span>
                            {!pkg.is_active && (
                              <span style={{
                                fontSize: "0.65rem", fontWeight: 600, color: "#f59e0b",
                                background: "#fef3c7", padding: "2px 8px", borderRadius: 10, fontFamily: FONT_FAMILY,
                              }}>
                                Inactive
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {canEdit && (
                            <ActionButton
                              label="Edit"
                              icon={Edit3}
                              small
                              variant="secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingPackage(pkg);
                                setShowFormModal(true);
                              }}
                            />
                          )}
                          {isExpanded ? <ChevronUp size={ICON_SIZES.sm} style={{ color: palette.text3 }} /> : <ChevronDown size={ICON_SIZES.sm} style={{ color: palette.text3 }} />}
                        </div>
                      </div>

                      {/* Expanded benefit list */}
                      {isExpanded && benefitList.length > 0 && (
                        <div style={{
                          marginTop: 10, paddingTop: 10, borderTop: `1px solid ${palette.cardBorder}`,
                        }}>
                          {benefitList.map((benefit, idx) => (
                            <div key={idx} style={{
                              display: "flex", alignItems: "center", gap: 8, padding: "4px 0",
                              fontSize: FONT_SIZES.xs, color: palette.text1, fontFamily: FONT_FAMILY,
                            }}>
                              <span style={{ color: palette.text3, width: 16, textAlign: "right", flexShrink: 0 }}>
                                {idx + 1}.
                              </span>
                              <span style={{ flex: 1 }}>{benefit.name}</span>
                              <span style={{
                                fontSize: "0.55rem", fontWeight: 600,
                                color: benefit.source === "catalog" ? palette.accent1 : "#f59e0b",
                                background: benefit.source === "catalog" ? `${palette.accent1}15` : "#fef3c7",
                                padding: "1px 5px", borderRadius: 6, fontFamily: FONT_FAMILY,
                              }}>
                                {benefit.source}
                              </span>
                            </div>
                          ))}
                          {pkg.notes && (
                            <p style={{ margin: "8px 0 0", fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY, fontStyle: "italic" }}>
                              {pkg.notes}
                            </p>
                          )}
                          {isAdmin && (
                            <div style={{ marginTop: 8, textAlign: "right" }}>
                              <ActionButton
                                label="Delete Package"
                                variant="danger"
                                small
                                onClick={() => setDeleteTarget(pkg)}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
          <p style={{ textAlign: "center", fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY, margin: "4px 0 0" }}>
            {packages.length} package{packages.length !== 1 ? "s" : ""}
          </p>
        </div>
      )}

      {/* Modals */}
      {showFormModal && (
        <PackageFormModal
          pkg={editingPackage}
          shows={shows}
          catalogTemplates={catalogTemplates}
          onSave={handleSave}
          onClose={() => { setShowFormModal(false); setEditingPackage(null); }}
        />
      )}
      {deleteTarget && (
        <ConfirmDialog
          title={`Delete "${deleteTarget.name}"?`}
          message="This package will be permanently deleted. Existing sponsorships using this package are not affected."
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// TAB 4: BENEFIT TEMPLATES (unchanged from Phase 3)
// ═══════════════════════════════════════════════════════════════

const BenefitTemplatesTab = ({
  canEdit,
  canDelete,
  userProfile,
  showMessage,
  showLoading,
  hideLoading,
  palette,
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteGuardPackages, setDeleteGuardPackages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      showLoading("Loading benefit templates...");
      const data = await benefitTemplatesService.getBenefitTemplates(true);
      setTemplates(data);
    } catch (error) {
      showMessage("Failed to load benefit templates", "error");
    } finally {
      setLoading(false);
      hideLoading();
    }
  }, [showMessage, showLoading, hideLoading]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const categories = useMemo(() => {
    const cats = new Set();
    templates.forEach((t) => { if (t.category) cats.add(t.category); });
    return Array.from(cats).sort();
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    let result = templates;
    if (!showInactive) result = result.filter((t) => t.is_active);
    if (filterCategory) result = result.filter((t) => t.category === filterCategory);
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((t) =>
        t.name.toLowerCase().includes(query) ||
        (t.description || "").toLowerCase().includes(query) ||
        (t.category || "").toLowerCase().includes(query)
      );
    }
    return result;
  }, [templates, showInactive, filterCategory, searchQuery]);

  const handleSave = async (templateData) => {
    if (editingTemplate) {
      await benefitTemplatesService.updateBenefitTemplate(editingTemplate.id, templateData);
      showMessage("Benefit template updated");
    } else {
      await benefitTemplatesService.createBenefitTemplate(
        templateData, CHAPTER_ID, userProfile?.id
      );
      showMessage("Benefit template created");
    }
    await loadTemplates();
  };

  const handleDeactivate = async (templateId) => {
    try {
      await benefitTemplatesService.deactivateBenefitTemplate(templateId);
      showMessage("Benefit template deactivated");
      await loadTemplates();
    } catch (error) { showMessage("Failed to deactivate template", "error"); }
  };

  const handleReactivate = async (templateId) => {
    try {
      await benefitTemplatesService.updateBenefitTemplate(templateId, { isActive: true });
      showMessage("Benefit template reactivated");
      await loadTemplates();
    } catch (error) { showMessage("Failed to reactivate template", "error"); }
  };

  const handleDeleteClick = async (template) => {
    const pkgs = await benefitTemplatesService.getPackagesUsingBenefit(template.name);
    setDeleteGuardPackages(pkgs.length > 0 ? pkgs : []);
    setDeleteTarget(template);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await benefitTemplatesService.deleteBenefitTemplate(deleteTarget.id);
      showMessage("Benefit template deleted", "warning");
      setDeleteTarget(null);
      setDeleteGuardPackages([]);
      await loadTemplates();
    } catch (error) { showMessage("Failed to delete template", "error"); }
  };

  const handleReorder = async (dragIndex, dropIndex) => {
    if (dragIndex === dropIndex) return;
    const reordered = [...filteredTemplates];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    try {
      await benefitTemplatesService.reorderBenefitTemplates(reordered.map((t) => t.id));
      await loadTemplates();
    } catch (error) { showMessage("Failed to reorder templates", "error"); }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: 40, color: palette.text3, fontFamily: FONT_FAMILY }}>Loading benefit templates...</div>;
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 6, background: palette.inputBg,
          border: `1px solid ${palette.inputBorder}`, borderRadius: 8, padding: "6px 10px", flex: "1 1 200px", maxWidth: 300,
        }}>
          <Search size={ICON_SIZES.sm} style={{ color: palette.text3, flexShrink: 0 }} />
          <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search benefits..."
            style={{ border: "none", outline: "none", background: "transparent", color: palette.inputColor, fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY, width: "100%" }} />
        </div>
        {categories.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Filter size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 8, fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY, background: palette.inputBg, color: palette.inputColor, border: `1px solid ${palette.inputBorder}`, cursor: "pointer" }}>
              <option value="">All Categories</option>
              {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
        )}
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: FONT_SIZES.xs, color: palette.text2, fontFamily: FONT_FAMILY, cursor: "pointer", whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} style={{ cursor: "pointer" }} />
          Show inactive
        </label>
        <div style={{ flex: 1 }} />
        {canEdit && <ActionButton label="Add Benefit" icon={Plus} small onClick={() => { setEditingTemplate(null); setShowFormModal(true); }} />}
      </div>

      {filteredTemplates.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 40 }}>
          <BookOpen size={ICON_SIZES.xxl} style={{ color: palette.text3, marginBottom: 8 }} />
          <p style={{ color: palette.text2, fontFamily: FONT_FAMILY, fontSize: FONT_SIZES.sm, margin: "8px 0 0" }}>
            {templates.length === 0 ? "No benefit templates yet. Add your first benefit to get started." : "No templates match your filters."}
          </p>
        </Card>
      ) : (
        <div>
          {filteredTemplates.map((template, index) => (
            <BenefitTemplateRow key={template.id} template={template} canEdit={canEdit} canDelete={canDelete}
              onEdit={(tmpl) => { setEditingTemplate(tmpl); setShowFormModal(true); }}
              onDeactivate={handleDeactivate} onReactivate={handleReactivate} onDelete={handleDeleteClick}
              dragHandleProps={canEdit ? {
                draggable: true,
                onDragStart: (e) => { e.dataTransfer.setData("text/plain", index.toString()); e.dataTransfer.effectAllowed = "move"; },
                onDragOver: (e) => e.preventDefault(),
                onDrop: (e) => { e.preventDefault(); handleReorder(parseInt(e.dataTransfer.getData("text/plain"), 10), index); },
              } : null}
            />
          ))}
          <p style={{ textAlign: "center", fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY, margin: "12px 0 0" }}>
            {filteredTemplates.length} benefit{filteredTemplates.length !== 1 ? "s" : ""}
            {showInactive && templates.filter((t) => !t.is_active).length > 0 && ` (${templates.filter((t) => !t.is_active).length} inactive)`}
          </p>
        </div>
      )}

      {showFormModal && (
        <BenefitTemplateFormModal template={editingTemplate} categories={categories} onSave={handleSave}
          onClose={() => { setShowFormModal(false); setEditingTemplate(null); }} />
      )}
      {deleteTarget && (
        <ConfirmDialog
          title={deleteGuardPackages.length > 0 ? `"${deleteTarget.name}" is in use` : `Delete "${deleteTarget.name}"?`}
          message={deleteGuardPackages.length > 0
            ? `This benefit is used in ${deleteGuardPackages.length} active package${deleteGuardPackages.length !== 1 ? "s" : ""}: ${deleteGuardPackages.map((p) => p.name).join(", ")}. Deleting it will not affect existing sponsorships, but it will be removed from these packages. Consider deactivating instead.`
            : "This benefit template will be permanently deleted. This does not affect existing sponsorships."}
          confirmLabel={deleteGuardPackages.length > 0 ? "Delete anyway" : "Delete"}
          onConfirm={handleDeleteConfirm}
          onCancel={() => { setDeleteTarget(null); setDeleteGuardPackages([]); }}
        />
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// TAB 3: SPONSORSHIPS
// ═══════════════════════════════════════════════════════════════

const SponsorshipsTab = ({ canEdit, isAdmin, canViewGifts, userProfile, showMessage, showLoading, hideLoading, palette }) => {
  const [sponsorships, setSponsorships] = useState([]);
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterShowId, setFilterShowId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      showLoading("Loading sponsorships...");
      const [sponsorshipData, showData] = await Promise.all([
        sponsorshipsService.getSponsorships({}),
        showsService.getShows(),
      ]);
      setSponsorships(sponsorshipData);
      setShows(showData);
    } catch (error) {
      showMessage("Failed to load sponsorships", "error");
    } finally {
      setLoading(false);
      hideLoading();
    }
  }, [showMessage, showLoading, hideLoading]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Filtered sponsorships ─────────────────────────────────
  const filtered = useMemo(() => {
    let result = sponsorships;

    if (filterShowId) {
      result = result.filter((s) => s.show_id === filterShowId);
    }

    if (filterStatus === "pending") {
      result = result.filter((s) => !s.isComplete);
    } else if (filterStatus === "complete") {
      result = result.filter((s) => s.isComplete);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((s) =>
        (s.credit_as_printed || "").toLowerCase().includes(query) ||
        (s.memberName || "").toLowerCase().includes(query)
      );
    }

    return result;
  }, [sponsorships, filterShowId, filterStatus, searchQuery]);

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await sponsorshipsService.deleteSponsorship(deleteTarget);
      showMessage("Sponsorship deleted", "warning");
      setDeleteTarget(null);
      await loadData();
    } catch (error) {
      showMessage("Failed to delete sponsorship", "error");
    }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: 40, color: palette.text3, fontFamily: FONT_FAMILY }}>Loading sponsorships...</div>;
  }

  return (
    <div>
      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {/* Search */}
        <div style={{
          display: "flex", alignItems: "center", gap: 6, background: palette.inputBg,
          border: `1px solid ${palette.inputBorder}`, borderRadius: 8, padding: "6px 10px", flex: "1 1 200px", maxWidth: 300,
        }}>
          <Search size={ICON_SIZES.sm} style={{ color: palette.text3, flexShrink: 0 }} />
          <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search sponsors..."
            style={{ border: "none", outline: "none", background: "transparent", color: palette.inputColor, fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY, width: "100%" }} />
        </div>

        {/* Show filter */}
        {shows.length > 0 && (
          <select value={filterShowId} onChange={(e) => setFilterShowId(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: 8, fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY, background: palette.inputBg, color: palette.inputColor, border: `1px solid ${palette.inputBorder}`, cursor: "pointer" }}>
            <option value="">All Shows</option>
            {shows.map((show) => (
              <option key={show.id} value={show.id}>{show.title} ({show.season})</option>
            ))}
          </select>
        )}

        {/* Status filter */}
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          style={{ padding: "6px 10px", borderRadius: 8, fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY, background: palette.inputBg, color: palette.inputColor, border: `1px solid ${palette.inputBorder}`, cursor: "pointer" }}>
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="complete">Complete</option>
        </select>
      </div>

      {/* Sponsorship cards */}
      {filtered.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 40 }}>
          <Users size={ICON_SIZES.xxl} style={{ color: palette.text3, marginBottom: 8 }} />
          <p style={{ color: palette.text2, fontFamily: FONT_FAMILY, fontSize: FONT_SIZES.sm, margin: "8px 0 0" }}>
            {sponsorships.length === 0
              ? "No sponsorships yet. Sponsorships are created when tagging a gift with a show and package."
              : "No sponsorships match your filters."}
          </p>
        </Card>
      ) : (
        <div>
          {filtered.map((sponsorship) => (
            <SponsorshipCard
              key={sponsorship.id}
              sponsorship={sponsorship}
              canEdit={canEdit}
              canDelete={isAdmin}
              canViewGifts={canViewGifts}
              chapterId={CHAPTER_ID}
              userId={userProfile?.id}
              onUpdate={loadData}
              onDelete={(id) => setDeleteTarget(id)}
              showMessage={showMessage}
            />
          ))}
          <p style={{ textAlign: "center", fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY, margin: "8px 0 0" }}>
            {filtered.length} sponsorship{filtered.length !== 1 ? "s" : ""}
            {filtered.length !== sponsorships.length && ` of ${sponsorships.length} total`}
          </p>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <ConfirmDialog
          title="Delete this sponsorship?"
          message="This sponsorship and all its benefit fulfillment records will be permanently deleted. The associated gift record is not affected."
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};
